/*
 * This file is part of the Akershus Eiendom Nettsted 2019 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import classnames from 'classnames';
import minValue from 'lodash/min';
import minBy from 'lodash/minBy';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';
import format from 'format-number';
import { useQueryState } from 'utils/querystringState';
import useEnhanced from 'utils/enhanced';
import Input from 'components/Input/Input';
import Magnifier from 'components/Icons/Magnifier/Magnifier';
import Property from 'components/Property/Property';
import Filter from './Filter/Filter';
import filterProperties from './filterProperties';
import './Properties.scss';

// Will trigger change of filter position
const onFilterChanged = debounce(() => {
  window.dispatchEvent(new Event('scroll'));
});

function getEmployeeRange(properties, max) {
  const values = properties.reduce(
    (accumulator, { premises }) => {
      const values = (premises || [])
        .map(({ employees }) => employees)
        .filter(employees => employees)
        .reduce((accumulator, { from }) => {
          return [...accumulator, from];
        }, []);

      return [...accumulator, ...values];
    },
    [0]
  );

  const min = minValue(values);
  return [min, max];
}

function getRentalRange(properties, max) {
  const values = properties.reduce(
    (accumulator, { premises }) => {
      const values = (premises || [])
        .map(({ rental }) => rental)
        .reduce((accumulator, rental) => {
          return [...accumulator, rental];
        }, []);

      return [...accumulator, ...values];
    },
    [0]
  );
  const min = minValue(values);
  return [min, max];
}

function countLocales(properties) {
  return properties.reduce(
    (accumulator, { premises }) => accumulator + (premises || []).length,
    0
  );
}

/**
 * This is the Properties component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 *
 * @return {JSX}
 */
function Properties({
  heading,
  intro,
  filter,
  display,
  search,
  order,
  properties,
  nomatch,
  formats,
}) {
  const filterNode = useRef(null);
  const defaultFormat = format(formats.default);
  const sizeFormat = format(formats.size);
  const enhanced = useEnhanced(1024);

  const path = encodeURIComponent(`${location.pathname}${location.search}`);
  const employeeRange = getEmployeeRange(properties, filter.employees.max);
  const rentalRange = getRentalRange(properties, filter.rental.max);

  const defaultState = {
    open: false,
    display: null,
    search: '',
    order: '',
    desc: false,
    area: [],
    type: null,
    employees: employeeRange,
    rental: rentalRange,
  };
  const [state, setState] = useQueryState(defaultState);

  const filteredProperties = filterProperties(
    properties,
    filter,
    state,
    defaultState
  ).map(({ url, premises, ...props }) => {
    if (isEqual(state, defaultState) || !premises.length) {
      return {
        ...props,
        premises,
        url: `${url}?b=${path}`,
      };
    }
    return {
      ...props,
      premises,
      url: `${url}?premise=${premises[0].id}&focus=true&b=${path}`,
    };
  });

  function getSize(premises, size) {
    const minSizePremise = minBy(premises, 'size');
    if (minSizePremise) {
      if (minSizePremise.size < size) {
        return `${defaultFormat(minSizePremise.size)} — ${sizeFormat(size)}`;
      }
    }

    return sizeFormat(size);
  }

  function onOpenClick() {
    setState({
      ...state,
      open: !state.open,
    });
  }

  function onDisplayClick(display) {
    setState({
      ...state,
      display: display === 'images' ? null : display,
    });
  }

  function onSearch({ target: { value } }) {
    setState({
      ...state,
      search: value,
    });
  }

  function onFilter(change) {
    const newState = { ...change };
    if (change.employees) {
      if (!isEqual(change.employees, defaultState.employees)) {
        newState.type = 'office';
      }
    }

    if (change.type && change.type !== 'office') {
      newState.employees = [...defaultState.employees];
    }

    setState(state => ({
      ...state,
      ...newState,
    }));
  }

  function onOrderBy({ target: { value } }) {
    if (value === state.order) {
      setState({
        ...state,
        desc: !state.desc,
      });
    } else {
      setState({
        ...state,
        order: value,
        desc: false,
      });
    }
  }

  function renderIntro() {
    if (!intro) return null;

    return (
      <div className="page-padding header-intro animate-2">
        <div className="body" dangerouslySetInnerHTML={{ __html: intro }} />
      </div>
    );
  }

  function renderFilter() {
    const { area, type, employees, rental, ...rest } = filter;
    return (
      <Filter
        ref={filterNode}
        {...rest}
        open={state.open}
        area={{
          ...area,
          value: state.area || '',
        }}
        type={{
          ...type,
          value: state.type || '',
        }}
        employees={{
          ...employees,
          range: {
            ...employees.range,
            min: employeeRange[0],
            max: employeeRange[1],
            defaultValue: employeeRange,
          },
          value: state.employees,
        }}
        rental={{
          ...rental,
          range: {
            ...rental.range,
            min: rentalRange[0],
            max: rentalRange[1],
            defaultValue: rentalRange,
          },
          value: state.rental,
        }}
        onOpen={onOpenClick}
        onChange={onFilter}
        onReset={() => {
          setState(defaultState);
        }}
      />
    );
  }

  function renderSorting() {
    if (enhanced) {
      return (
        <CSSTransition
          key="sort"
          timeout={0}
          enter={false}
          exit={false}
          unmountOnExit
        >
          <div className="page-padding sort">
            {order.list.map(({ id, label }) => (
              <div key={id} className={id}>
                <button
                  title={label}
                  className={classnames({
                    active: id === state.order,
                    desc: id === state.order && state.desc,
                  })}
                  onClick={() => {
                    onOrderBy({ target: { value: id } });
                  }}
                >
                  <span>{label}</span>
                </button>
              </div>
            ))}
          </div>
        </CSSTransition>
      );
    }

    return (
      <CSSTransition
        key="order-select"
        timeout={0}
        enter={false}
        exit={false}
        unmountOnExit
      >
        <div className="page-padding order-select">
          <select value={state.order} onChange={onOrderBy}>
            <option value="">{order.title}</option>
            {order.list.map(({ id, label }) => (
              <option key={id} value={id}>
                {label}
              </option>
            ))}
          </select>
        </div>
      </CSSTransition>
    );
  }

  function renderNoMatch() {
    if (filteredProperties.length > 0) return null;

    function renderContent() {
      return (
        <div>
          <h2>{nomatch.title}</h2>
          <div className="body">
            <p>
              {nomatch.body}&nbsp;
              <button
                onClick={() => {
                  setState(state => ({
                    ...state,
                    open: true,
                  }));
                  if (filterNode.current) {
                    filterNode.current.focus();
                  }
                }}
              >
                {nomatch.button}
              </button>
            </p>
          </div>
        </div>
      );
    }

    if (state.display === 'list') {
      return (
        <CSSTransition
          timeout={600}
          unmountOnExit
          onEntered={onFilterChanged}
          onExited={onFilterChanged}
        >
          <div className="page-padding row no-match">{renderContent()}</div>
        </CSSTransition>
      );
    }

    return (
      <CSSTransition
        timeout={600}
        unmountOnExit
        onEntered={onFilterChanged}
        onExited={onFilterChanged}
      >
        <div className="property no-match">{renderContent()}</div>
      </CSSTransition>
    );
  }

  function renderDisplay() {
    if (state.display === 'list') {
      return (
        <section className="property-list-list">
          {renderFilter()}
          <TransitionGroup
            key="rows"
            component="div"
            className="items animate-3"
            appear
          >
            {renderSorting()}
            {renderNoMatch()}
            {filteredProperties.map(
              ({ id, url, name, areas, type, size, vacancy, premises }) => (
                <CSSTransition
                  key={id}
                  timeout={600}
                  unmountOnExit
                  onEntered={onFilterChanged}
                  onExited={onFilterChanged}
                >
                  <a className="page-padding row" href={url}>
                    {(() => {
                      if (enhanced) {
                        return (
                          <>
                            <div className="name">{name}</div>
                            <div className="area">
                              {(Array.isArray(areas) && areas[0]) || ''}
                            </div>
                            <div className="type">{type}</div>
                            <div className="size">
                              {getSize(premises, size)}
                            </div>
                            <div className="vacancy">{vacancy}</div>
                          </>
                        );
                      }

                      return (
                        <>
                          <div className="name">{name}</div>
                          <div className="size">{getSize(premises, size)}</div>
                          <div className="area-type">
                            <div className="area">
                              {(Array.isArray(areas) && areas[0]) || ''}
                            </div>
                            <div className="type">{type}</div>
                          </div>

                          <div className="vacancy">{vacancy}</div>
                        </>
                      );
                    })()}
                  </a>
                </CSSTransition>
              )
            )}
          </TransitionGroup>
        </section>
      );
    }

    return (
      <section className="page-padding property-list-images">
        {renderFilter()}
        <TransitionGroup
          key="properties"
          component="div"
          className="items animate-3"
          appear
        >
          {renderNoMatch()}
          {filteredProperties.map(({ id, url, size, premises, ...props }) => (
            <CSSTransition
              key={id}
              timeout={600}
              unmountOnExit
              onEntered={onFilterChanged}
              onExited={onFilterChanged}
            >
              <Property
                url={url}
                premises={premises}
                {...props}
                size={getSize(premises, size)}
              />
            </CSSTransition>
          ))}
        </TransitionGroup>
      </section>
    );
  }

  return (
    <div className="animate-visible">
      <h1 className="page-padding animate-1">
        <strong>{countLocales(filteredProperties)}</strong> {heading}
      </h1>
      {renderIntro()}
      <div className={classnames('properties', { 'filter-open': state.open })}>
        <header className="page-padding animate-2">
          <Input
            className="azur search-input"
            {...search}
            icon={<Magnifier />}
            value={state.search}
            onChange={onSearch}
          />
          <div className="display">
            <div className="label" aria-labelledby="display">
              {display.title}
            </div>
            <div id="display" className="choices">
              {display.list.map(({ id, label }) => (
                <button
                  key={id}
                  className={classnames({
                    active: id === (state.display || 'images'),
                  })}
                  onClick={() => {
                    onDisplayClick(id);
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </header>
        <div>{renderDisplay()}</div>
      </div>
    </div>
  );
}

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
Properties.propTypes = {
  heading: PropTypes.string.isRequired,
  intro: PropTypes.string,
  filter: PropTypes.shape({
    area: PropTypes.object.isRequired,
    type: PropTypes.object.isRequired,
    employees: PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      max: PropTypes.number.isRequired,
      format: PropTypes.object.isRequired,
      range: PropTypes.object,
    }).isRequired,
    rental: PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      max: PropTypes.number.isRequired,
      range: PropTypes.object,
    }).isRequired,
    labels: PropTypes.object.isRequired,
  }).isRequired,
  display: PropTypes.shape({
    title: PropTypes.string.isRequired,
    list: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
      })
    ).isRequired,
  }).isRequired,
  search: PropTypes.object.isRequired,
  order: PropTypes.shape({
    title: PropTypes.string.isRequired,
    list: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
      })
    ).isRequired,
  }),
  properties: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      sizeString: PropTypes.string,
      areas: PropTypes.array,
      premises: PropTypes.array,
    })
  ).isRequired,
  formats: PropTypes.shape({
    default: PropTypes.object.isRequired,
    size: PropTypes.object.isRequired,
  }).isRequired,
  nomatch: PropTypes.shape({
    title: PropTypes.string.isRequired,
    body: PropTypes.string.isRequired,
    button: PropTypes.string.isRequired,
  }).isRequired,
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
Properties.defaultProps = {
  filter: {
    labels: {
      all: 'All',
      open: 'Open filter',
      close: 'Close filter',
    },
  },
  display: {
    title: 'Show as',
    list: [],
  },
  search: {},
  order: {
    title: 'Name',
    list: [],
  },
  properties: [],
  nomatch: {
    title: 'No match',
    body: 'Please try again with another filtering',
    button: 'here',
  },
  formats: {
    default: {},
    size: {
      suffix: ' sq.m',
    },
  },
};

export default Properties;
